import { createForm } from "@tanstack/solid-form";
import { createMemo, onMount, untrack } from "solid-js";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { TagInput, TagInputSuggestions } from "@/components/shared/input";
import { Card, CardContent } from "@/components/ui/card";
import { FieldContainer, FieldContainerLabel } from "@/components/ui/input";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { devLog } from "@/libs/dev-log";
import {
  boundUnknownSkillTerms,
  MAX_SKILL_TERM_LENGTH,
  MAX_SKILL_TERMS,
  skillTermsSchema,
} from "@/libs/skill-input";
import { useOnboardingSkillChipsFor } from "../../api/skill-chips";
import profilePic from "../../assets/profile-pic.png";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";

const skillsValidationSchema = z.object({
  skills: skillTermsSchema.min(1),
});

export const Skills = () => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();
  const form = createForm(() => ({
    defaultValues: untrack(() => ({
      skills: store.skills.value,
    })),
    onSubmit: ({ value }) => {
      devLog(value);
    },
  }));

  // «Avanti» resta spento con 0 competenze: la categoria si sceglie nello step
  // `sector`, qui si legge soltanto.
  onMount(() => {
    if (boundUnknownSkillTerms(store.skills.value).length === 0) {
      setStore("skills", {
        value: [],
        error: "Invalid skills",
      });
    }
  });

  // I settori scelti nello step 2: il secondario conta solo se c'è ed è diverso
  // dal principale. Cambiarli cambia le chiavi delle query, non le competenze.
  const sectors = createMemo((): (string | null)[] => {
    const primary = store.sector.value.job_category;
    const secondary = store.sector.value.job_category_secondary;
    return [primary, secondary && secondary !== primary ? secondary : null];
  });

  // I chip vengono dal catalogo per i settori scelti (S7.1, AC18), il
  // principale per primo.
  const suggestions = useOnboardingSkillChipsFor(sectors);

  // «Suggerite per X» / «Suggerite per X e Y», solo se ci sono chip da mostrare.
  const suggestionsLabel = createMemo(() => {
    if (suggestions().length === 0) return undefined;
    const labels = sectors()
      .filter((key): key is string => !!key)
      .map(categoryLabel);
    if (labels.length === 0) return undefined;
    return t("onboarding.steps.skills.suggestedFor", {
      labels: labels.join(" e "),
    });
  });

  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.skills.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.skills.description")}
        </OnboardingDescription>
      </div>
      <div class="flex gap-4 justify-between items-start">
        <div class="flex flex-col gap-8 w-full md:w-1/2">
          <form.Field
            name="skills"
            validators={{ onChange: skillsValidationSchema.shape.skills }}
            listeners={{
              onChange: ({ value }) => {
                if (value.length > 0) {
                  setStore("skills", {
                    value: value,
                    error: null,
                  });
                } else {
                  setStore("skills", {
                    value: [],
                    error: "Invalid skills",
                  });
                }
              },
            }}
          >
            {(field) => (
              <FieldContainer class="flex flex-col gap-2">
                <FieldContainerLabel for={field().name}>
                  {t("onboarding.steps.skills.yourSkills")}
                </FieldContainerLabel>
                <TagInput
                  fieldApi={field()}
                  class="bg-white"
                  maxTags={MAX_SKILL_TERMS}
                  maxTagLength={MAX_SKILL_TERM_LENGTH}
                />
                <TagInputSuggestions
                  fieldApi={field()}
                  suggestions={suggestions()}
                  label={suggestionsLabel()}
                  maxTags={MAX_SKILL_TERMS}
                  maxTagLength={MAX_SKILL_TERM_LENGTH}
                />
              </FieldContainer>
            )}
          </form.Field>
        </div>
        <Card class="hidden md:block w-1/3 rounded-lg shadow-none bg-primary/5">
          <CardContent class="p-4">
            <div class="flex flex-col gap-2">
              <img src={profilePic} alt="LinkedIn" width={100} />
              <p class="text-xl text-primary">
                {t("onboarding.steps.skills.card.text")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
